import { useRef, useState } from "react";
import { Camera, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, del, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { UserAvatar } from "@/components/UserAvatar";

const TYPES = ["image/png", "image/jpeg"];
const MAX = 2 * 1024 * 1024;

export const AvatarUpload = () => {
  const { profile, refreshProfile } = useAuth();
  const input = useRef(null);
  const [busy, setBusy] = useState(null);
  const stamp = profile.avatar_updated_at;

  const run = async (kind, fn) => {
    setBusy(kind);
    try {
      await fn();
      await refreshProfile();
      toast.success(kind === "upload" ? "Profile picture saved" : "Profile picture removed");
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!TYPES.includes(file.type)) { toast.error("Profile picture must be a PNG or JPG image"); return; }
    if (file.size > MAX) { toast.error("Profile picture is too large (max 2 MB)"); return; }
    const body = new FormData();
    body.append("file", file);
    run("upload", () => api.post("/me/avatar", body));
  };

  return (
    <div className="flex flex-wrap items-center gap-4" data-testid="avatar-upload">
      <UserAvatar userId={profile.id} stamp={stamp} name={profile.full_name} className="h-20 w-20 bg-slate-100 text-slate-400" testId="avatar-preview" />
      <div className="space-y-2">
        <p className="text-xs text-slate-500">PNG or JPG, up to 2 MB. Cropped to a circle.</p>
        <div className="flex flex-wrap gap-2">
          <input ref={input} type="file" accept="image/png,image/jpeg" className="hidden" onChange={onFile} data-testid="avatar-file-input" />
          <Button type="button" variant="outline" disabled={!!busy} onClick={() => input.current?.click()} data-testid="avatar-upload-button">
            {busy === "upload" ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Camera className="mr-1 h-4 w-4" />}{stamp ? "Change picture" : "Upload picture"}
          </Button>
          {stamp && (
            <Button type="button" variant="ghost" disabled={!!busy} onClick={() => run("remove", () => del("/me/avatar"))} className="text-rose-700" data-testid="avatar-remove-button">
              {busy === "remove" ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Trash2 className="mr-1 h-4 w-4" />}Remove
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
